import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Users, Target, Globe, Award, ArrowRight, Shield, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SEO title="About Us" description="Learn about Training Solutions - India's leading platform connecting corporate trainers with companies worldwide." path="/about" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/search?type=trainers" className="text-slate-600 hover:text-slate-900 font-medium">Find Trainers</Link>
              <Link to="/search?type=jobs" className="text-slate-600 hover:text-slate-900 font-medium">Browse Requirements</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 font-medium">Pricing</Link>
            </nav>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-16">
        {/* Hero */}
        <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Bridging the Gap Between<br/>Trainers & Companies</h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Training Solutions is India's premier marketplace for corporate training. We connect expert trainers with organizations that need world-class training programs — from leadership development to technical upskilling.
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: Target, title: 'Our Mission', desc: 'To democratize access to quality corporate training by building a transparent marketplace where talent meets opportunity — regardless of geography.' },
                { icon: Globe, title: 'Global Reach', desc: 'Operating across 60+ countries with 400+ cities covered. We support training in 70+ languages, connecting local expertise with global demand.' },
                { icon: Sparkles, title: 'AI-Powered', desc: 'Our AI-driven matching engine analyzes skills, experience, location, and budget to recommend the best trainer-company pairings automatically.' }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 p-8 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                    <item.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="font-outfit text-3xl font-bold text-slate-900 text-center mb-12">How It Works</h2>
            <div className="space-y-8">
              {[
                { step: '1', title: 'Create Your Profile', desc: 'Sign up as a Trainer or Company. Fill in your expertise, location, and preferences.', color: 'bg-blue-600' },
                { step: '2', title: 'Post or Discover', desc: 'Companies post training requirements. Trainers browse opportunities or get matched by our AI.', color: 'bg-emerald-600' },
                { step: '3', title: 'Connect & Train', desc: 'Review profiles, discuss details, and book training sessions — all through the platform.', color: 'bg-purple-600' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-6">
                  <div className={`${item.color} w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="font-outfit text-3xl font-bold text-slate-900 text-center mb-12">Why Choose Training Solutions</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Users, title: 'Verified Trainers', desc: 'Every trainer profile is reviewed for quality and authenticity.' },
                { icon: Shield, title: 'Secure Platform', desc: 'Your data is protected with enterprise-grade security measures.' },
                { icon: Award, title: 'Free for 6 Months', desc: 'All features are completely free during our launch period.' },
                { icon: Globe, title: 'Pan-India & Global', desc: 'Find trainers locally or internationally across 60+ countries.' }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-outfit font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="font-outfit text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-blue-100 mb-8 text-lg">Join thousands of trainers and companies already using Training Solutions.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?type=trainer">
                <Button className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 h-12 font-medium">
                  Join as Trainer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register?type=corporate">
                <Button className="bg-blue-700 text-white hover:bg-blue-800 border border-blue-500 rounded-full px-8 h-12 font-medium">
                  Join as Company <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-outfit font-bold">Training Solutions</span>
                </div>
                <p className="text-sm text-slate-400">India's leading corporate training marketplace.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Platform</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <Link to="/search?type=trainers" className="block hover:text-white">Find Trainers</Link>
                  <Link to="/search?type=jobs" className="block hover:text-white">Browse Requirements</Link>
                  <Link to="/pricing" className="block hover:text-white">Pricing</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Company</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <Link to="/about" className="block hover:text-white">About Us</Link>
                  <Link to="/contact" className="block hover:text-white">Contact</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Legal</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <Link to="/privacy" className="block hover:text-white">Privacy Policy</Link>
                  <Link to="/terms" className="block hover:text-white">Terms of Service</Link>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
              © {new Date().getFullYear()} Training Solutions. All rights reserved.
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
